// Annotation geometry: bounds, page-op remapping (rotate/delete/move/
// insert/geometry), drag translation and SVG path helpers.
import {
  geometryTransform,
  isMarkup,
  NOTE_SIZE,
  quadBounds,
  type Annotation,
  type DashStyle,
  type PageInfo,
  type PageOp,
  type Quad,
} from "./protocol.ts";

/** SVG dash-array matching the exported annotation's dash pattern. */
export function dashArray(dash: DashStyle | undefined, w: number): string | undefined {
  if (dash === "dashed") return `${w * 3} ${w * 2}`;
  if (dash === "dotted") return `${w} ${w * 2}`;
  return undefined;
}

/** Bounding box of any overlay annotation, fitz page space. */
export function annotBounds(a: Annotation): [number, number, number, number] {
  if (isMarkup(a) || a.kind === "redact") {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const q of a.quads) {
      const b = quadBounds(q);
      x0 = Math.min(x0, b.x);
      y0 = Math.min(y0, b.y);
      x1 = Math.max(x1, b.x + b.width);
      y1 = Math.max(y1, b.y + b.height);
    }
    return [x0, y0, x1, y1];
  }
  switch (a.kind) {
    case "text":
      return [a.x, a.y, a.x + a.width, a.y + a.height];
    case "note":
      return [a.x, a.y, a.x + NOTE_SIZE, a.y + NOTE_SIZE];
    case "stamp":
      return [a.x, a.y, a.x + a.width, a.y + a.height];
    case "link":
      return a.rect;
    case "ink": {
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (let i = 0; i + 1 < a.points.length; i += 2) {
        x0 = Math.min(x0, a.points[i]!);
        x1 = Math.max(x1, a.points[i]!);
        y0 = Math.min(y0, a.points[i + 1]!);
        y1 = Math.max(y1, a.points[i + 1]!);
      }
      return [x0, y0, x1, y1];
    }
    case "shape":
      return [
        Math.min(a.x1, a.x2),
        Math.min(a.y1, a.y2),
        Math.max(a.x1, a.x2),
        Math.max(a.y1, a.y2),
      ];
  }
}

// ─────────────────── annotation remapping for page ops ───────────────────

/** Rotate a point 90° within a w×h page (both spaces top-left, y-down). */
export function rotatePoint(x: number, y: number, w: number, h: number, cw: boolean): [number, number] {
  return cw ? [h - y, x] : [y, w - x];
}

/** New top-left for a box that stays axis-aligned after rotation (text
 *  and note boxes don't rotate their content — only their anchor moves). */
export function rotateBoxAnchor(
  x: number,
  y: number,
  bw: number,
  bh: number,
  w: number,
  h: number,
  cw: boolean
): [number, number] {
  const corners = [
    rotatePoint(x, y, w, h, cw),
    rotatePoint(x + bw, y, w, h, cw),
    rotatePoint(x, y + bh, w, h, cw),
    rotatePoint(x + bw, y + bh, w, h, cw),
  ];
  return [Math.min(...corners.map((c) => c[0])), Math.min(...corners.map((c) => c[1]))];
}

/** Keep the annotation overlay glued to its content across a page op.
 *  `pagesBefore` are the page dims the coordinates were expressed in. */
export function remapAnnots(annots: Annotation[], op: PageOp, pagesBefore: PageInfo[]): Annotation[] {
  switch (op.kind) {
    // Crop keeps page indices; doPageOp applies the origin shift the
    // worker reports (it alone knows the resulting boxes).
    case "crop":
      return annots;
    case "rotate": {
      const info = pagesBefore[op.pageIndex];
      if (!info) return annots;
      const { width: w, height: h } = info;
      const cw = op.clockwise;
      return annots.map((a) => {
        if (a.pageIndex !== op.pageIndex) return a;
        if (isMarkup(a)) {
          return {
            ...a,
            quads: a.quads.map((q) => {
              const out: number[] = [];
              for (let i = 0; i < 8; i += 2) {
                const [x, y] = rotatePoint(q[i]!, q[i + 1]!, w, h, cw);
                out.push(x, y);
              }
              return out as unknown as Quad;
            }),
          };
        }
        switch (a.kind) {
          case "ink": {
            const points: number[] = [];
            for (let i = 0; i + 1 < a.points.length; i += 2) {
              const [x, y] = rotatePoint(a.points[i]!, a.points[i + 1]!, w, h, cw);
              points.push(x, y);
            }
            return { ...a, points };
          }
          case "shape": {
            const [x1, y1] = rotatePoint(a.x1, a.y1, w, h, cw);
            const [x2, y2] = rotatePoint(a.x2, a.y2, w, h, cw);
            return { ...a, x1, y1, x2, y2 };
          }
          case "text": {
            const [x, y] = rotateBoxAnchor(a.x, a.y, a.width, a.height, w, h, cw);
            return { ...a, x, y };
          }
          case "note": {
            const [x, y] = rotateBoxAnchor(a.x, a.y, NOTE_SIZE, NOTE_SIZE, w, h, cw);
            return { ...a, x, y };
          }
          case "stamp": {
            const [x, y] = rotateBoxAnchor(a.x, a.y, a.width, a.height, w, h, cw);
            return { ...a, x, y };
          }
          case "link": {
            const [ax, ay] = rotatePoint(a.rect[0], a.rect[1], w, h, cw);
            const [bx, by] = rotatePoint(a.rect[2], a.rect[3], w, h, cw);
            return {
              ...a,
              rect: [
                Math.min(ax, bx),
                Math.min(ay, by),
                Math.max(ax, bx),
                Math.max(ay, by),
              ] as [number, number, number, number],
            };
          }
          case "redact":
            return {
              ...a,
              quads: a.quads.map((q) => {
                const out: number[] = [];
                for (let i = 0; i < 8; i += 2) {
                  const [x, y] = rotatePoint(q[i]!, q[i + 1]!, w, h, cw);
                  out.push(x, y);
                }
                return out as unknown as Quad;
              }),
            };
        }
      });
    }
    case "delete":
      return annots
        .filter((a) => a.pageIndex !== op.pageIndex)
        // links pointing AT the deleted page are dead — drop them too
        .filter((a) => !(a.kind === "link" && a.page === op.pageIndex))
        .map((a) => (a.pageIndex > op.pageIndex ? { ...a, pageIndex: a.pageIndex - 1 } : a))
        .map((a) =>
          a.kind === "link" && a.page !== undefined && a.page > op.pageIndex
            ? { ...a, page: a.page - 1 }
            : a
        );
    case "move": {
      const movedIndex = (p: number) => {
        if (p === op.from) return op.to;
        let out = p;
        if (out > op.from) out -= 1;
        if (out >= op.to) out += 1;
        return out;
      };
      return annots.map((a) => {
        const p = movedIndex(a.pageIndex);
        if (a.kind === "link") {
          const page = a.page !== undefined ? movedIndex(a.page) : undefined;
          if (p === a.pageIndex && page === a.page) return a;
          return { ...a, pageIndex: p, page };
        }
        return p === a.pageIndex ? a : { ...a, pageIndex: p };
      });
    }
    case "insertBlank":
      return annots.map((a) => {
        const pageIndex = a.pageIndex >= op.at ? a.pageIndex + 1 : a.pageIndex;
        if (a.kind === "link") {
          const page =
            a.page !== undefined && a.page >= op.at ? a.page + 1 : a.page;
          if (pageIndex === a.pageIndex && page === a.page) return a;
          return { ...a, pageIndex, page };
        }
        return pageIndex === a.pageIndex ? a : { ...a, pageIndex };
      });
    case "geometry":
      return annots.map((a) => {
        if (op.target !== "all" && a.pageIndex !== op.target) return a;
        const info = pagesBefore[a.pageIndex];
        if (!info) return a;
        const { s, tx, ty } = geometryTransform(info.width, info.height, op);
        const px = (x: number) => s * x + tx;
        const py = (y: number) => s * y + ty;
        if (isMarkup(a) || a.kind === "redact") {
          return {
            ...a,
            quads: a.quads.map(
              (q) =>
                q.map((v, i) => (i % 2 === 0 ? px(v) : py(v))) as unknown as Quad
            ),
          };
        }
        switch (a.kind) {
          case "ink":
            return {
              ...a,
              strokeWidth: Math.max(0.25, a.strokeWidth * s),
              points: a.points.map((v, i) => (i % 2 === 0 ? px(v) : py(v))),
            };
          case "shape":
            return {
              ...a,
              strokeWidth: Math.max(0.25, a.strokeWidth * s),
              x1: px(a.x1),
              y1: py(a.y1),
              x2: px(a.x2),
              y2: py(a.y2),
            };
          case "text":
            return {
              ...a,
              x: px(a.x),
              y: py(a.y),
              width: a.width * s,
              height: a.height * s,
              size: Math.max(1, a.size * s),
            };
          case "note":
            // icon keeps its size; only the anchor moves
            return { ...a, x: px(a.x), y: py(a.y) };
          case "stamp":
            return {
              ...a,
              x: px(a.x),
              y: py(a.y),
              width: a.width * s,
              height: a.height * s,
            };
          case "link":
            return {
              ...a,
              rect: [px(a.rect[0]), py(a.rect[1]), px(a.rect[2]), py(a.rect[3])] as [
                number,
                number,
                number,
                number,
              ],
            };
        }
      });
  }
}

/** Shift an annotation by a page-space delta (drag-to-move). Markup
 *  stays put — it's glued to the text it marks. */
export function translateAnnot(a: Annotation, dx: number, dy: number): Annotation {
  switch (a.kind) {
    case "text":
    case "note":
    case "stamp":
      return { ...a, x: a.x + dx, y: a.y + dy };
    case "ink":
      return { ...a, points: a.points.map((v, i) => (i % 2 === 0 ? v + dx : v + dy)) };
    case "shape":
      return { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy };
    case "link":
      return {
        ...a,
        rect: [a.rect[0] + dx, a.rect[1] + dy, a.rect[2] + dx, a.rect[3] + dy] as [
          number,
          number,
          number,
          number,
        ],
      };
    case "redact":
      return {
        ...a,
        quads: a.quads.map(
          (q) => q.map((v, i) => (i % 2 === 0 ? v + dx : v + dy)) as unknown as Quad
        ),
      };
    default:
      return a;
  }
}

/** Rotate an annotation about (cx, cy) by `deg` degrees clockwise.
 *
 *  Two different jobs, depending on what the annotation IS:
 *
 *   - Geometry made of points — markup and redaction quads, ink strokes,
 *     line and arrow endpoints — rotates EXACTLY. Quads are arbitrary
 *     quadrilaterals in PDF and ink is a path, so a rotated one is still
 *     a perfectly ordinary annotation that any reader draws correctly.
 *   - Boxes that PDF stores as an axis-aligned rectangle (text, stamps,
 *     rect/ellipse shapes) cannot express rotation in their geometry, so
 *     the box's CENTRE moves and the angle is recorded on the annotation
 *     for the appearance stream to apply (see writeRotatedAppearance).
 *
 *  Notes and links are left alone: a rotated note icon means nothing, and
 *  a link's clickable area has to stay axis-aligned to be clickable.
 */
export function rotateAnnot(
  a: Annotation,
  cx: number,
  cy: number,
  deg: number
): Annotation {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const rot = (x: number, y: number): [number, number] => {
    const dx = x - cx;
    const dy = y - cy;
    return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
  };
  const rotQuads = (quads: readonly Quad[]): Quad[] =>
    quads.map((q) => {
      const out: number[] = [];
      for (let i = 0; i + 1 < q.length; i += 2) {
        const [x, y] = rot(q[i]!, q[i + 1]!);
        out.push(x, y);
      }
      return out as unknown as Quad;
    });

  if (isMarkup(a)) return { ...a, quads: rotQuads(a.quads) };
  switch (a.kind) {
    case "redact":
      return { ...a, quads: rotQuads(a.quads) };
    case "ink": {
      const points: number[] = [];
      for (let i = 0; i + 1 < a.points.length; i += 2) {
        const [x, y] = rot(a.points[i]!, a.points[i + 1]!);
        points.push(x, y);
      }
      return { ...a, points };
    }
    case "shape": {
      if (a.shape === "line" || a.shape === "arrow") {
        // The endpoints ARE the geometry, so this is exact.
        const [x1, y1] = rot(a.x1, a.y1);
        const [x2, y2] = rot(a.x2, a.y2);
        return { ...a, x1, y1, x2, y2 };
      }
      // A Square/Circle keeps its size; its centre swings round and the
      // angle rides along for the appearance to apply.
      const w = Math.abs(a.x2 - a.x1);
      const h = Math.abs(a.y2 - a.y1);
      const [mx, my] = rot(Math.min(a.x1, a.x2) + w / 2, Math.min(a.y1, a.y2) + h / 2);
      return {
        ...a,
        x1: mx - w / 2,
        y1: my - h / 2,
        x2: mx + w / 2,
        y2: my + h / 2,
        rotation: normaliseAngle((a.rotation ?? 0) + deg),
      };
    }
    case "text":
    case "stamp": {
      const [mx, my] = rot(a.x + a.width / 2, a.y + a.height / 2);
      return {
        ...a,
        x: mx - a.width / 2,
        y: my - a.height / 2,
        rotation: normaliseAngle((a.rotation ?? 0) + deg),
      };
    }
    default:
      return a;
  }
}

/** Degrees folded into [0, 360). */
export function normaliseAngle(deg: number): number {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

/** Can this annotation be rotated at all? Notes have no orientation and
 *  links must stay axis-aligned, so a selection of only those offers no
 *  rotation rather than pretending. */
export function canRotate(a: Annotation): boolean {
  return a.kind !== "note" && a.kind !== "link";
}

/** Shift EVERYTHING on a page, markup included — for crop, where the
 *  page's whole coordinate origin moves and the text moves with it. */
export function shiftAnnotWithPage(a: Annotation, dx: number, dy: number): Annotation {
  if (isMarkup(a)) {
    return {
      ...a,
      quads: a.quads.map(
        (q) => q.map((v, i) => (i % 2 === 0 ? v + dx : v + dy)) as unknown as Quad
      ),
    };
  }
  return translateAnnot(a, dx, dy);
}

export function pointsToPath(pts: number[]): string {
  let d = `M ${pts[0]} ${pts[1]}`;
  for (let i = 2; i + 1 < pts.length; i += 2) d += ` L ${pts[i]} ${pts[i + 1]}`;
  return d;
}
